import React, { useEffect, useRef, useState } from 'react';
import { tx } from '../utils/i18n';
import { useLanguage } from '../contexts/LanguageContext';

const StatsSection = () => {
  const { t, currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const sectionRef = useRef(null);

  const statsData = [
    {
      label: t('deepLinksGenerated'),
      value: "10M+",
      unit: ""
    },
    {
      label: t('conversionRate'),
      value: "12",
      unit: "%"
    },
    {
      label: t('premiumPartners'),
      value: "200+",
      unit: ""
    },
    {
      label: t('trustingClients'),
      value: "10000+",
      unit: ""
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const animateCounters = () => {
    statsData.forEach((stat, index) => {
      const target = parseInt(stat.value.replace(/[^0-9]/g, ''));
      let current = 0;
      const increment = target / 50; // Adjust speed here
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current);
          return newCounters;
        });
      }, 50);
    });
  };

  const formatValue = (value, originalValue, unit) => {
    if (originalValue.includes('B+')) {
      return `${(value / 1000).toFixed(1)}B+`;
    } else if (originalValue.includes('M+')) {
      return `${value}M+`;
    } else if (originalValue.includes('+')) {
      return `${value}+`;
    }
    return `${value}${unit}`;
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {tx(currentLanguage, 'Des résultats qui parlent d\'eux-mêmes', 'Results that speak for themselves', 'Ergebnisse, die für sich sprechen')}
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            {tx(currentLanguage, 'Rejoignez des milliers d\'annonceurs qui font confiance à OptimHub pour maximiser leurs performances publicitaires.', 'Join thousands of advertisers who trust OptimHub to maximize their advertising performance.', 'Schließen Sie sich Tausenden von Werbetreibenden an, die OptimHub vertrauen, um ihre Werbeleistung zu maximieren.')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {statsData.map((stat, index) => (
            <div 
              key={index}
              className="text-center group hover:transform hover:scale-105 transition-all duration-300"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all duration-300">
                <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
                  {formatValue(counters[index], stat.value, stat.unit)}
                </div>
                <div className="text-blue-100 font-medium text-lg">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom section */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-2 text-blue-200">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              {tx(currentLanguage, 'Données mises à jour en temps réel', 'Real-time updated data', 'Daten in Echtzeit aktualisiert')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;